import { useState, FormEvent, MouseEvent } from "react";

type SubmitFunctionAsync<V> = (values: V) => Promise<any>;
type SubmitFunctionSync<V> = (values: V) => any;
type SubmitFunction<V> = SubmitFunctionAsync<V> | SubmitFunctionSync<V>;

export type GenericObject<T = any> = { [key: string]: T };

export type UseFormProps<V> = {
  initial: V;
  validate: (name: keyof V, value: any) => string;
  submit: SubmitFunction<V>;
};

export const useForm = <V extends GenericObject>({
  initial,
  validate,
  submit,
}: UseFormProps<V>) => {
  type E = { [key in keyof V]: string };

  const getInitialErrors = () => {
    const errs = {} as E;

    for (const key in initial) {
      if (Object.hasOwn(initial, key)) {
        errs[key] = "";
      }
    }

    return errs;
  };

  const initialErrors = getInitialErrors();

  const [values, setValues] = useState<V>(initial);
  const [errors, setErrors] = useState<E>(getInitialErrors());

  const onChange = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const onBlur = (name: string, value: string) => {
    const error = validate(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const onSubmit = async (
    e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>
  // eslint-disable-next-line require-await
  ) => {
    e.preventDefault();

    const errorMessages = initialErrors;
    let errorExists = false;

    for (const key of Object.keys(initialErrors)) {
      const value = values[key as keyof V];
      const error = validate(key as keyof V, value);

      errorMessages[key as keyof E] = error;
      errorExists ||= Boolean(error);
    }

    setErrors(errorMessages);

    if (errorExists) {
      return;
    }

    // eslint-disable-next-line consistent-return
    return submit(values);
  };

  const getInputPropsFor = <T>(name: keyof V) => ({
      name,
      value: values[name] as T,
      error: errors[name] as string,
      onChange,
      onBlur,
    });

  const hasChanged = JSON.stringify(initial) !== JSON.stringify(values);
  const hasErrors = Object.values(errors).some(Boolean);

  return {
    values,
    errors,
    hasChanged,
    hasErrors,
    onBlur,
    onChange,
    onSubmit,
    getInputPropsFor,
  };
};
